"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { HugeiconsIcon } from "@hugeicons/react";
import Bookmark01Icon from "@hugeicons/core-free-icons/Bookmark01Icon";
import FavouriteIcon from "@hugeicons/core-free-icons/FavouriteIcon";
import Image01Icon from "@hugeicons/core-free-icons/Image01Icon";
import Message01Icon from "@hugeicons/core-free-icons/Message01Icon";
import SentIcon from "@hugeicons/core-free-icons/SentIcon";
import Video01Icon from "@hugeicons/core-free-icons/Video01Icon";
import { cn } from "@/lib/utils";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";

/**
 * Mockup of an Instagram post → DM funnel powered by Zapify.
 *
 * It animates on a 30 s loop:
 *   • 0 – 3.5 s  Instagram post (View 1), comments cascade in.
 *   • 3.5 s      Sliding transition to the DM screen (View 2).
 *   • 3.5 s+     DM thread builds up — auto-reply, follow gating,
 *                product card, tagging, email capture, follow-ups.
 *
 * The cursor that taps the Follow button is purely CSS-driven (see globals.css).
 */

type StepKey =
  | "sep_intro"
  | "msg_1"
  | "msg_user_link"
  | "msg_2"
  | "msg_user_following"
  | "msg_3";

const SCHEDULE: { t: number; key: StepKey }[] = [
  { t: 2800, key: "sep_intro" },
  { t: 3200, key: "msg_1" },
  { t: 7000, key: "msg_user_link" },
  { t: 8000, key: "msg_2" },
  { t: 11000, key: "msg_user_following" },
  { t: 12000, key: "msg_3" },
];

const VIEW_SWITCH_AT = 2500;

const PROFILE_IMG =
  "https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=44&h=44&q=75&crop=faces&fit=crop";
const COMMENTER_1 =
  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=44&h=44&q=75&crop=faces&fit=crop";
const COMMENTER_2 =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=44&h=44&q=75&crop=faces&fit=crop";
const COMMENTER_3 =
  "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=44&h=44&q=75&crop=faces&fit=crop";
const POST_IMG =
  "https://images.unsplash.com/photo-1515886657613-9f3515b0c78f?w=600&q=80";

const IG_COMMENTS = [
  {
    avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=44&h=44&q=75&crop=faces&fit=crop",
    name: "user_one",
    verified: false,
    text: "Link Please",
    time: "1d",
    likes: 12,
    replies: 2,
  },
  {
    avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=44&h=44&q=75&crop=faces&fit=crop",
    name: "user_two",
    verified: false,
    text: "Where can I get it?",
    time: "1d",
    likes: 8,
    replies: 1,
  },
  {
    avatar: "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?w=44&h=44&q=75&crop=faces&fit=crop",
    name: "user_three",
    verified: false,
    text: "Can you send me the link?",
    time: "1d",
    likes: 15,
    replies: 3,
  },
];

export function PhoneMockup({ className }: { className?: string }) {
  const [active, setActive] = useState<Set<StepKey>>(() => new Set());
  const [view, setView] = useState<"post" | "dm">("post");
  const [commentsOpen, setCommentsOpen] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const timeouts: number[] = [];
    timeouts.push(window.setTimeout(() => setView("dm"), VIEW_SWITCH_AT));

    for (const step of SCHEDULE) {
      timeouts.push(
        window.setTimeout(() => {
          setActive((prev) => {
            const next = new Set(prev);
            next.add(step.key);
            return next;
          });
        }, step.t),
      );
    }

    return () => {
      timeouts.forEach((id) => window.clearTimeout(id));
    };
  }, []);

  // Auto-scroll only when content overflows — keeps msg_1 visible at top initially.
  useEffect(() => {
    const node = bodyRef.current;
    if (!node) return;
    if (node.scrollHeight > node.clientHeight) {
      node.scrollTop = node.scrollHeight;
    }
  }, [active, view]);

  const isOn = (k: StepKey) => active.has(k);

  const followHidden = useMemo(() => isOn("msg_user_link"), [active]);

  return (
    <div className={cn("relative inline-flex justify-center", className)}>
      <div className="phone-bg-circle" aria-hidden />

      <div className="phone-shell" role="img" aria-label="Zapify DM automation demo">
        <div className="phone-notch" aria-hidden />

        <div className="phone-screen">
          <div className="phone-views" data-view={view}>
            {/* ────────── View 1 — Instagram post ────────── */}
            <div className="phone-view">
              {/* Header */}
              <div className="ig-header">
                <span className="ig-avatar">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={PROFILE_IMG} alt="" width={32} height={32} />
                </span>
                <div className="ig-header-name">
                  <span className="ig-username">
                    zapify.pro
                    {/* verified badge */}
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden style={{display:"inline",verticalAlign:"middle",marginLeft:"3px"}}>
                      <circle cx="12" cy="12" r="12" fill="#3897f0"/>
                      <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </span>
                  <span className="ig-subname">and who &bull;</span>
                </div>
                <span className="ig-dots">···</span>
              </div>

              {/* Post image — mobile-mockup.webp */}
              <div className="ig-post-img-wrap">
                <div className="ig-code-post">
                  <div className="ig-code-post-bg-grid" />
                  
                  {/* Floating elements */}
                  <div className="ig-code-floating-bubble">
                    <HugeiconsIcon icon={Message01Icon} size={24} color="currentColor" />
                  </div>
                  
                  <div className="ig-code-floating-pill">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path></svg>
                    <span>LINK</span>
                    <div className="ig-code-cursor">
                      <svg viewBox="0 0 18 22" fill="currentColor">
                        <path d="M2 1.5 16 11l-6.6 1.7L13 19.5 10 21l-3.5-7L1 17V1.5Z" stroke="#fff" strokeWidth="1.2" />
                      </svg>
                    </div>
                  </div>

                  {/* Sparkles */}
                  <div className="ig-code-sparkle s1"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0l2 8 8 2-8 2-2 8-2-8-8-2 8-2z"/></svg></div>
                  <div className="ig-code-sparkle s2"><svg viewBox="0 0 24 24" fill="currentColor"><path d="M12 0l2 8 8 2-8 2-2 8-2-8-8-2 8-2z"/></svg></div>

                  <div className="ig-code-post-content">
                    <h3 className="ig-code-text-top">Comment</h3>
                    <h2 className="ig-code-text-keyword">“LINK”</h2>
                    <h3 className="ig-code-text-bottom">to get access</h3>
                  </div>

                  {/* Zapify Logo Watermark */}
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src="/assets/zapify-logo.png" alt="Zapify Logo" className="ig-code-logo" />
                </div>
                {/* carousel arrow right */}
                <div className="ig-carousel-arrow" aria-hidden>
                  <svg viewBox="0 0 24 24" fill="none"><path d="M9 18l6-6-6-6" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                </div>
                {/* slide dots */}
                <div className="ig-dots-row" aria-hidden>
                  <span className="ig-dot active" />
                  <span className="ig-dot" />
                  <span className="ig-dot" />
                </div>
              </div>

              {/* Actions row */}
              <div className="ig-actions">
                <HugeiconsIcon icon={FavouriteIcon} size={18} color="currentColor" />
                <button
                  type="button"
                  className="ig-comment-btn"
                  aria-label="View comments"
                  onClick={() => setCommentsOpen(true)}
                >
                  <HugeiconsIcon icon={Message01Icon} size={18} color="currentColor" />
                </button>
                <HugeiconsIcon icon={SentIcon} size={18} color="currentColor" />
                <HugeiconsIcon icon={Bookmark01Icon} size={18} color="currentColor" />
              </div>

              {/* Like count */}
              <div className="ig-likes">2,341 likes</div>

              {/* Comments preview */}
              <div className="ig-comments">
                <div className="ig-comment c1">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={COMMENTER_1} alt="" width={22} height={22} />
                  <span>
                    <span className="ig-comment-name">user_one</span>
                    Link Please
                  </span>
                </div>
                <div className="ig-comment c2">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={COMMENTER_2} alt="" width={22} height={22} />
                  <span>
                    <span className="ig-comment-name">user_two</span>
                    Where can I get it?
                  </span>
                </div>
                <div className="ig-comment c3">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={COMMENTER_3} alt="" width={22} height={22} />
                  <span>
                    <span className="ig-comment-name">user_three</span>
                    Can you send me the link?
                  </span>
                </div>
                <button
                  type="button"
                  className="ig-view-all-btn"
                  onClick={() => setCommentsOpen(true)}
                >
                  View all 52 comments
                </button>
              </div>
            </div>

            {/* ────────── View 2 — DM conversation ────────── */}
            <div className="phone-view">
              <div className="dm-header">
                <span className="ig-avatar">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={PROFILE_IMG} alt="" width={32} height={32} />
                </span>
                <span className="ig-username">your_brand</span>
                <div className="dm-header-actions">
                  <button
                    type="button"
                    className="dm-follow-btn"
                    data-hidden={followHidden}
                    aria-hidden
                    tabIndex={-1}
                  >
                    Follow
                  </button>
                  <HugeiconsIcon icon={Video01Icon} size={16} color="currentColor" />
                </div>
              </div>

              <div className="dm-body" ref={bodyRef}>
                <div className={cn("dm-sep", isOn("sep_intro") && "is-active")}>
                  <span>zapify.pro messaged you about a comment that you made on their post. <strong>See post</strong></span>
                </div>

                <div className={cn("dm-msg-in", isOn("msg_1") && "is-active")}>
                  <span className="dm-msg-title">
                    <span aria-hidden>✨</span>
                    Want the free access link?
                  </span>
                  <span className="dm-msg-body">
                    Tap below and I&apos;ll send it instantly.
                  </span>
                  <div className="dm-btn-group">
                    <button type="button" className="dm-action-btn" aria-hidden tabIndex={-1}>Send the Link</button>
                  </div>
                </div>

                <div className={cn("dm-msg-out", isOn("msg_user_link") && "is-active")}>
                  Send the Link
                </div>

                <div className={cn("dm-msg-in", isOn("msg_2") && "is-active")}>
                  <span className="dm-msg-title">
                    <span aria-hidden>🔒</span>
                    Quick check 👀
                  </span>
                  <span className="dm-msg-body">
                    Follow the profile to unlock the content.
                  </span>
                  <div className="dm-btn-group">
                    <button type="button" className="dm-action-btn" aria-hidden tabIndex={-1}>Follow Profile</button>
                    <button type="button" className="dm-action-btn" aria-hidden tabIndex={-1}>Done Following</button>
                  </div>
                </div>

                <div className={cn("dm-msg-out", isOn("msg_user_following") && "is-active")}>
                  Done Following
                </div>

                <div className={cn("dm-msg-in", isOn("msg_3") && "is-active")}>
                  <span className="dm-msg-title">
                    <span aria-hidden>🎉</span>
                    Thanks for following 🙌
                  </span>
                  <span className="dm-msg-body">
                    Your access is ready below.
                  </span>
                  <div className="dm-btn-group">
                    <button type="button" className="dm-action-btn" aria-hidden tabIndex={-1}>Open Access</button>
                  </div>
                </div>
              </div>

              <div className="dm-footer">
                <div className="dm-input">
                  <span>Message…</span>
                  <HugeiconsIcon
                    icon={Image01Icon}
                    size={14}
                    color="currentColor"
                    className="ml-auto opacity-60"
                  />
                </div>
                <HugeiconsIcon icon={FavouriteIcon} size={18} color="currentColor" className="dm-footer-heart" />
              </div>
            </div>
          </div>

          {/* Animated cursor that taps the Follow button */}
          <div className="phone-cursor" aria-hidden>
            <svg viewBox="0 0 18 22" fill="currentColor">
              <path d="M2 1.5 16 11l-6.6 1.7L13 19.5 10 21l-3.5-7L1 17V1.5Z" stroke="#fff" strokeWidth="1.2" />
            </svg>
          </div>
        </div>
      </div>
      {/* ────────── Comments Drawer ────────── */}
      <Drawer open={commentsOpen} onOpenChange={setCommentsOpen}>
        <DrawerContent className="max-h-[80vh]">
          <DrawerHeader className="border-b border-border/40 pb-3">
            <DrawerTitle className="text-center text-[15px]">Comments</DrawerTitle>
          </DrawerHeader>
          <div className="overflow-y-auto px-4 py-3 space-y-5">
            {IG_COMMENTS.map((c, i) => (
              <div key={i} className="flex items-start gap-3">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={c.avatar} alt={c.name} className="size-9 shrink-0 rounded-full object-cover" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] leading-snug text-foreground">
                    <span className="font-semibold mr-1">{c.name}</span>
                    {c.verified && (
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" aria-hidden style={{display:"inline",verticalAlign:"middle",marginRight:"4px"}}>
                        <circle cx="12" cy="12" r="12" fill="#3897f0"/>
                        <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    )}
                    {c.text}
                  </p>
                  <div className="mt-1 flex items-center gap-4 text-[11px] text-muted-foreground">
                    <span>{c.time}</span>
                    {(c.likes ?? 0) > 0 && <span>{c.likes} {c.likes === 1 ? "like" : "likes"}</span>}
                    <span className="cursor-pointer font-semibold">Reply</span>
                    {c.verified && <span className="cursor-pointer">See translation</span>}
                  </div>
                  {"replies" in c && (c.replies ?? 0) > 0 && (
                    <button type="button" className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                      <span className="h-px w-8 bg-border inline-block" />
                      View all {c.replies} {(c.replies ?? 0) === 1 ? "reply" : "replies"}
                    </button>
                  )}
                </div>
                <button type="button" aria-label="Like comment" className="shrink-0 mt-0.5">
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none"><path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09A6.03 6.03 0 0116.5 3C19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" stroke="currentColor" strokeWidth="1.5" className="text-muted-foreground"/></svg>
                </button>
              </div>
            ))}
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}

export default PhoneMockup;
